import React from 'react';
import { Upload } from 'lucide-react';
import { EditableText } from '../components/EditableText';
import { BuilderButton } from '../components/BuilderButton';
import { ImageDropZone } from '../components/ImageDropZone';
import { MediaDropZone } from '../components/MediaDropZone';

export interface AboutContent {
  teamPhoto: string;
  teamVideo?: string; // Optional video URL for about section
  companyName: string;
  bio: string;
}

interface AboutSectionProps {
  content: AboutContent;
  onUpdate: (updates: Partial<AboutContent>) => void;
  organizationId: string;
  isPreview: boolean;
  onTeamMediaUpload?: (file: File, type: 'image' | 'video') => void;
  pendingMediaType?: 'image' | 'video' | null;
  accentColor?: string;
}

export const AboutSection: React.FC<AboutSectionProps> = ({
  content,
  onUpdate,
  organizationId,
  isPreview,
  onTeamMediaUpload,
  pendingMediaType,
  accentColor,
}) => {
  // Determine if we have video content
  const isVideoMedia = pendingMediaType === 'video' || (content.teamVideo && !content.teamPhoto);

  const mediaUrl = content.teamVideo || content.teamPhoto;

  return (
    <div className="w-full bg-white py-16">
      <div className="max-w-7xl mx-auto px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left: Team Photo/Video Upload */}
          <div className="relative">
            {mediaUrl ? (
              <div className="relative group">
                {isVideoMedia ? (
                  <video
                    src={mediaUrl}
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="w-full h-[500px] object-cover rounded-2xl shadow-xl"
                  />
                ) : (
                  <img
                    src={mediaUrl}
                    alt="Team"
                    className="w-full h-[500px] object-cover rounded-2xl shadow-xl"
                  />
                )}
                {!isPreview && (
                  <div className="absolute inset-0 rounded-2xl overflow-hidden">
                    <MediaDropZone
                      onMediaDrop={(file, type) => {
                        if (onTeamMediaUpload) {
                          onTeamMediaUpload(file, type);
                        }
                      }}
                      isUploading={false}
                      label="Drop photo or video"
                      acceptVideo={true}
                    />
                  </div>
                )}
              </div>
            ) : (
              <div className="w-full h-[500px] bg-gradient-to-br from-gray-200 to-gray-100 rounded-2xl relative overflow-hidden">
                {!isPreview && (
                  <MediaDropZone
                    onMediaDrop={(file, type) => {
                      if (onTeamMediaUpload) {
                        onTeamMediaUpload(file, type);
                      }
                    }}
                    isUploading={false}
                    label="Drop team photo or video"
                    acceptVideo={true}
                    showAlways
                  />
                )}
              </div>
            )}
          </div>

          {/* Right: About Content */}
          <div className="space-y-6">
            {/* Header Tagline - Static */}
            <div className="mb-4">
              <div
                className="font-bold text-sm uppercase tracking-wider"
                style={{ color: accentColor || '#FACC15' }}
              >
                ABOUT US
              </div>
            </div>

            {/* Company Name */}
            <div className="text-gray-900 text-5xl font-bold leading-tight">
              <EditableText
                value={content.companyName}
                onChange={(value: string) => onUpdate({ companyName: value })}
                isPreview={isPreview}
                className="leading-tight"
              />
            </div>

            {/* Bio/Description */}
            <div className="mt-6">
              <div className="text-gray-700 text-lg leading-relaxed">
                <EditableText
                  value={content.bio}
                  onChange={(value: string) => onUpdate({ bio: value })}
                  isPreview={isPreview}
                  multiline
                  className="leading-relaxed"
                />
              </div>
            </div>

            {/* Action Buttons - Static */}
            <div className="flex items-center gap-4 pt-6">
              <BuilderButton accentColor={accentColor}>LEARN MORE</BuilderButton>
              <BuilderButton accentColor={accentColor}>CONTACT US</BuilderButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
